import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getCategories, getCities, getProducts, isEnquiryCategory, isRealEstateCategory } from '../marketApi';
import { useCart } from '../CartContext';
import { useSellerDomain } from '../SellerDomainContext';
import { formatPrice } from '../currency';

const COLORS = [
  '#2563eb', '#7c3aed', '#db2777', '#d97706',
  '#16a34a', '#0891b2', '#dc2626', '#65a30d',
];

function colorFor(str) {
  if (!str) return COLORS[0];
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) & 0xffff;
  return COLORS[h % COLORS.length];
}

function SkeletonCard() {
  return (
    <div className="product-card" style={{ cursor: 'default' }}>
      <div className="skeleton" style={{ width: '100%', height: 180 }} />
      <div className="product-card-body">
        <div className="skeleton" style={{ height: 14, width: '85%', marginBottom: 6 }} />
        <div className="skeleton" style={{ height: 12, width: '60%' }} />
      </div>
      <div className="product-card-footer">
        <div className="skeleton" style={{ height: 20, width: 60 }} />
        <div className="skeleton" style={{ height: 30, width: 90, borderRadius: 8 }} />
      </div>
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const { addToCart, cart } = useCart();
  const sellerDomain = useSellerDomain();

  const [cities, setCities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCity, setSelectedCity] = useState(
    () => localStorage.getItem('smb_city') || ''
  );
  const [selectedCategory, setSelectedCategory] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState('');
  const [addedMap, setAddedMap] = useState({});
  const [geoCity, setGeoCity] = useState('');  // city that was auto-detected

  const SIZE = 20;
  const searchTimer = useRef(null);
  const geoRan = useRef(false);

  useEffect(() => {
    getCities().then(setCities).catch(() => {});
  }, []);

  useEffect(() => {
    if (sellerDomain === undefined) return; // still detecting
    getCategories(sellerDomain?.id).then(setCategories).catch(() => {});
  }, [sellerDomain]);

  // Auto-detect city from IP address — silent, no browser permission prompt
  useEffect(() => {
    if (cities.length === 0) return;
    if (geoRan.current) return;
    geoRan.current = true;

    if (localStorage.getItem('smb_city')) return;
    if (sessionStorage.getItem('smb_geo_tried')) return;

    sessionStorage.setItem('smb_geo_tried', '1');

    (async () => {
      try {
        const res = await fetch('https://ip-api.com/json/?fields=status,city');
        const data = await res.json();
        if (data.status !== 'success' || !data.city) return;

        const detected = data.city.toLowerCase();
        const match = cities.find((c) => {
          const cv = (c.city || c).toLowerCase();
          return cv === detected || cv.includes(detected) || detected.includes(cv);
        });

        if (match) {
          const cityVal = match.city || match;
          setGeoCity(cityVal);
          setSelectedCity(cityVal);
          localStorage.setItem('smb_city', cityVal);
        }
      } catch (_) { /* fail silently */ }
    })();
  }, [cities]);

  const fetchProducts = useCallback(
    async (pageNum, reset) => {
      if (sellerDomain === undefined) return; // still detecting domain
      setLoading(true);
      setError('');
      try {
        const params = {
          page: pageNum,
          size: SIZE,
        };
        if (selectedCity) params.city = selectedCity;
        if (selectedCategory) params.categoryId = selectedCategory;
        if (search) params.search = search;
        if (sellerDomain) params.sellerId = sellerDomain.id;

        const data = await getProducts(params);
        const list = Array.isArray(data) ? data : (data.data || data.content || data.products || []);
        const total = data.total ?? (Array.isArray(data) ? data.length : list.length);
        const noMore =
          list.length < SIZE ||
          (data.last === true) ||
          (data.totalPages !== undefined && pageNum >= data.totalPages - 1) ||
          ((page + 1) * SIZE >= total);

        if (reset) {
          setProducts(list);
        } else {
          setProducts((prev) => [...prev, ...list]);
        }
        setHasMore(!noMore);
      } catch (e) {
        setError(e.message || 'Failed to load products');
      } finally {
        setLoading(false);
      }
    },
    [selectedCity, selectedCategory, search, sellerDomain]
  );

  // Reset and reload on filter change (including when seller domain resolves)
  useEffect(() => {
    if (sellerDomain === undefined) return;
    setPage(0);
    setProducts([]);
    setHasMore(true);
    fetchProducts(0, true);
  }, [selectedCity, selectedCategory, search, sellerDomain]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleCityChange(c) {
    setSelectedCity(c);
    localStorage.setItem('smb_city', c);
  }

  function handleSearchInput(v) {
    setSearchInput(v);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setSearch(v), 400);
  }

  function handleLoadMore() {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchProducts(nextPage, false);
  }

  function handleAddToCart(e, product) {
    e.stopPropagation();
    addToCart(product, 1);
    setAddedMap((prev) => ({ ...prev, [product.id]: true }));
    setTimeout(() => setAddedMap((prev) => ({ ...prev, [product.id]: false })), 1500);
  }

  function cartQty(id) {
    const item = cart.find((i) => i.productId === id);
    return item ? item.quantity : 0;
  }

  return (
    <div>
      {/* Hero */}
      <div className="hero">
        {sellerDomain ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, justifyContent: 'center', marginBottom: 6 }}>
              {sellerDomain.logo_url && (
                <img src={sellerDomain.logo_url} alt={sellerDomain.business_name}
                  style={{ height: 56, maxWidth: 140, objectFit: 'contain', borderRadius: 8, background: 'rgba(255,255,255,0.15)', padding: 4 }} />
              )}
              <h1 style={{ margin: 0 }}>{sellerDomain.business_name}</h1>
            </div>
            <p>{sellerDomain.city ? `Serving ${sellerDomain.city} — ` : ''}Shop our full range of products</p>
          </>
        ) : (
          <>
            <h1>Fresh products delivered to your door</h1>
            <p>Shop local — vegetables, groceries, spices and more</p>
          </>
        )}
        <div className="hero-search">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <select
              className="form-control"
              value={selectedCity}
              onChange={(e) => { handleCityChange(e.target.value); setGeoCity(''); }}
              style={{ maxWidth: 160 }}
            >
              <option value="">All Cities</option>
              {cities.map((c) => (
                <option key={c.city || c} value={c.city || c}>
                  {c.city || c}
                </option>
              ))}
            </select>
            {geoCity && selectedCity === geoCity && (
              <span style={{
                fontSize: 11, color: '#16a34a', background: 'rgba(240,253,244,0.9)',
                padding: '2px 8px', borderRadius: 20, display: 'inline-flex',
                alignItems: 'center', gap: 4, whiteSpace: 'nowrap',
              }}>
                📍 Auto-detected
                <button
                  onClick={() => { setGeoCity(''); handleCityChange(''); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#16a34a', padding: 0, lineHeight: 1 }}
                  title="Clear auto-detection"
                >✕</button>
              </span>
            )}
          </div>
          <input
            className="form-control"
            placeholder="Search products or shops..."
            value={searchInput}
            onChange={(e) => handleSearchInput(e.target.value)}
          />
          <button
            className="btn btn-accent"
            onClick={() => setSearch(searchInput)}
          >
            Search
          </button>
        </div>
      </div>

      <div className="page-container">
        {/* Categories */}
        {categories.length > 0 && (
          <div className="category-scroll">
            <button
              className={`chip${selectedCategory === '' ? ' active' : ''}`}
              onClick={() => setSelectedCategory('')}
            >
              All
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                className={`chip${selectedCategory === String(cat.id) ? ' active' : ''}`}
                onClick={() =>
                  setSelectedCategory(
                    selectedCategory === String(cat.id) ? '' : String(cat.id)
                  )
                }
              >
                {cat.icon && <span style={{ marginRight: 4 }}>{cat.icon}</span>}
                {cat.name}
              </button>
            ))}
          </div>
        )}

        {/* Error */}
        {error && <div className="alert alert-error">{error}</div>}

        {/* Shop match banner — shown when all results are from one seller */}
        {search && products.length > 0 && (() => {
          const names = [...new Set(products.map(p => p.seller_name || p.sellerName).filter(Boolean))];
          if (names.length !== 1) return null;
          const lc = search.toLowerCase();
          if (!names[0].toLowerCase().includes(lc)) return null;
          const rep = products[0];
          const logoUrl = rep.seller_logo_url || rep.sellerLogoUrl || null;
          const sellerDesc = rep.seller_description || null;
          const sellerTnC  = rep.seller_terms     || null;
          return (
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#e8f0fe', border: '1px solid #c5d9f8', borderRadius: 10, padding: '10px 16px' }}>
                {logoUrl ? (
                  <img src={logoUrl} alt={names[0]}
                    style={{ height: 40, maxWidth: 100, objectFit: 'contain', borderRadius: 6, background: '#fff', padding: 3, border: '1px solid #c5d9f8' }} />
                ) : (
                  <span style={{ fontSize: 22 }}>🏪</span>
                )}
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{names[0]}</div>
                  <div style={{ fontSize: 12, color: '#555' }}>Showing all products from this shop</div>
                </div>
                <button style={{ marginLeft: 'auto', background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#5f6368' }}
                  onClick={() => { setSearchInput(''); setSearch(''); }}>×</button>
              </div>
            </div>
          );
        })()}


        {/* Product grid */}
        {loading && products.length === 0 ? (
          <div className="product-grid">
            {Array.from({ length: 8 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : products.length === 0 && !loading ? (
          <div className="empty-state">
            <div className="empty-state-icon">🏪</div>
            <h3>No products found</h3>
            <p style={{ marginTop: 6 }}>
              {search || selectedCity || selectedCategory
                ? 'Try changing your filters or search term'
                : 'No products available yet'}
            </p>
          </div>
        ) : (
          <>
            <div className="product-grid">
              {products.map((product) => (
                <div
                  key={product.id}
                  className="product-card"
                  onClick={() => navigate(`/product/${product.id}`)}
                >
                  {(product.image_url || product.imageUrl) ? (
                    <img
                      src={product.image_url || product.imageUrl}
                      alt={product.name}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div
                    className="product-card-img-placeholder"
                    style={{
                      background: colorFor(product.name),
                      display: (product.image_url || product.imageUrl) ? 'none' : 'flex',
                    }}
                  >
                    {(product.name || 'P').charAt(0).toUpperCase()}
                  </div>
                  <div className="product-card-body">
                    <div className="product-card-name">{product.name}</div>
                    <div className="product-card-seller">
                      {product.seller_name || product.sellerName || product.seller?.businessName || ''}{' '}
                      {(product.city || product.seller?.city) && (
                        <span className="badge badge-muted" style={{ fontSize: 10 }}>
                          {product.city || product.seller?.city}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="product-card-footer">
                    <div>
                      {isRealEstateCategory(product.category_name || product.categoryName || product.category?.name) ? (
                        (product.original_price || product.originalPrice) > product.price ? (
                          <span className="price" style={{ fontSize: 13 }}>
                            {formatPrice(product.price, product.currency)}
                            <span style={{ fontWeight: 400, color: 'var(--muted)', margin: '0 3px' }}>–</span>
                            {formatPrice(product.original_price || product.originalPrice, product.currency)}
                          </span>
                        ) : (
                          <span className="price" style={{ fontSize: 13 }}>
                            {formatPrice(product.price, product.currency)}
                            <span style={{ fontSize: 10, fontWeight: 400, color: 'var(--muted)', marginLeft: 3 }}>onwards</span>
                          </span>
                        )
                      ) : (
                        <>
                          <span className="price">
                            {formatPrice(product.price, product.currency)}
                            <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--muted)' }}>
                              /{product.unit || 'piece'}
                            </span>
                          </span>
                          {(product.original_price || product.originalPrice) > product.price && (
                            <div className="original-price">{formatPrice(product.original_price || product.originalPrice, product.currency)}</div>
                          )}
                        </>
                      )}
                    </div>
                    {isEnquiryCategory(product.category_name || product.categoryName || product.category?.name) ? (
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={(e) => { e.stopPropagation(); navigate(`/product/${product.id}`); }}
                      >
                        📩 Enquire
                      </button>
                    ) : (
                      <button
                        className={`btn btn-sm ${
                          addedMap[product.id] ? 'btn-accent' : 'btn-primary'
                        }`}
                        onClick={(e) => handleAddToCart(e, product)}
                        disabled={product.stock === 0}
                      >
                        {addedMap[product.id]
                          ? '✓ Added'
                          : cartQty(product.id) > 0
                          ? `+Add (${cartQty(product.id)})`
                          : 'Add'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Load more */}
            {hasMore && (
              <div style={{ textAlign: 'center', marginTop: 24 }}>
                <button
                  className="btn btn-outline"
                  onClick={handleLoadMore}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}

            {!hasMore && products.length > 0 && (
              <div
                style={{
                  textAlign: 'center',
                  color: 'var(--muted)',
                  fontSize: 13,
                  marginTop: 24,
                  paddingBottom: 12,
                }}
              >
                You've seen all {products.length} products
              </div>
            )}
          </>
        )}
      </div>

      {/* Seller CTA banner — hidden on seller's own domain */}
      {!sellerDomain && <div
        style={{
          background: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%)',
          color: '#fff',
          padding: '40px 24px',
          textAlign: 'center',
          marginTop: 32,
        }}
      >
        <div style={{ fontSize: 40, marginBottom: 10 }}>🏪</div>
        <h2 style={{ fontWeight: 800, fontSize: 22, marginBottom: 8 }}>
          Want to sell on SmaBizZ?
        </h2>
        <p style={{ opacity: 0.85, fontSize: 14, maxWidth: 420, margin: '0 auto 20px' }}>
          Register as a seller, list your products and start receiving cash-on-delivery orders in your city.
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link to="/seller/register" className="btn btn-accent btn-lg" style={{ minWidth: 160 }}>
            Register as Seller
          </Link>
          <Link
            to="/seller/login"
            className="btn btn-lg"
            style={{ minWidth: 140, background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.4)' }}
          >
            Seller Login
          </Link>
        </div>
      </div>}
    </div>
  );
}
